import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';

const TransparencyPage: React.FC = () => {
  const [showPreviewForm, setShowPreviewForm] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    businessName: '',
    email: '',
    phone: '',
    serviceType: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Transparency | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Our commitment to transparency. No hidden fees, full ownership, clear pricing. See exactly what you get with BusinessWebsitePro.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'transparent pricing, honest pricing, no hidden fees, clear pricing, website pricing transparency');
    }
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formElement = e.target as HTMLFormElement;
    const formDataToSend = new FormData(formElement);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4qdq67iu80n4l8qo160', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setShowPreviewForm(false);
          setFormSubmitted(false);
          setFormData({
            customerName: '',
            businessName: '',
            email: '',
            phone: '',
            serviceType: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const comparisonData = [
    { feature: 'Transparent pricing', us: true, others: false },
    { feature: 'Pay after preview', us: true, others: false },
    { feature: 'No gimmicks', us: true, others: false },
    { feature: 'Clear add-ons', us: true, others: false },
    { feature: 'Monthly plans optional', us: true, others: false }
  ];

  const testimonials = [
    {
      quote: "Everything was clear. No hidden fees.",
      author: "Sarah M.",
      business: "Local Bakery Owner"
    },
    {
      quote: "I paid exactly what was shown — nothing extra.",
      author: "David L.",
      business: "Real Estate Agent"
    },
    {
      quote: "Finally a website company that doesn't trick you.",
      author: "Jennifer K.",
      business: "Wellness Coach"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Schema.org Structured Data for Transparency */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Our Transparency Promise - BusinessWebsitePro",
          "description": "Learn about our transparent pricing, no hidden fees policy, and commitment to honest business practices. See exactly what you're paying for with BusinessWebsitePro.",
          "url": "https://businesswebsitepro.com/transparency",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://businesswebsitepro.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Transparency",
                "item": "https://businesswebsitepro.com/transparency"
              }
            ]
          }
        })}
      </script>

      {/* FAQ Schema for Q&A Section */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What exactly am I paying for?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You're paying for professional website design, development, and ongoing support. The one-time fee covers the complete website build, and the monthly fee covers hosting, security, backups, and maintenance. Every cost is clearly itemized with no hidden fees."
              }
            },
            {
              "@type": "Question",
              "name": "Are there any hidden fees?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely not. We believe in complete transparency. All costs are clearly stated upfront, including the one-time design fee and monthly support costs. If any additional work is needed beyond the agreed scope, we'll always get your approval first."
              }
            },
            {
              "@type": "Question",
              "name": "Can I see the cost breakdown?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! We provide a detailed cost breakdown for every project, showing exactly what you're paying for - from design hours to hosting costs to specific features. We want you to understand the value you're receiving."
              }
            },
            {
              "@type": "Question",
              "name": "What happens if I want to cancel?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can cancel your monthly support plan anytime with 30 days notice. We'll provide all your website files and help you transition to another provider. The website you paid for is yours to keep forever."
              }
            },
            {
              "@type": "Question",
              "name": "Do you own my website?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Once you pay for your website, you own it completely. We provide the hosting and support, but the website, content, and all files belong to you. You can take it anywhere, anytime."
              }
            }
          ]
        })}
      </script>
      
      {/* No Payment Ribbon */}
      <div className="fixed top-20 right-6 z-40 bg-gradient-to-r from-[#C4F547] via-[#FFD700] to-[#FFA500] text-gray-900 px-6 py-3 rounded-full font-bold text-sm shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-300 animate-pulse cursor-pointer">
        <i className="ri-shield-check-line mr-2 text-lg"></i>
        No Upfront Payment
      </div>
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/02df202d6aaf295fbde6d158355d8de9.jpg"
            alt="Transparency and trust in business"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Our Transparency Commitment
          </h1>
        </div>
      </section>

      {/* Our Promise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-3xl p-10 md:p-16 shadow-lg border border-teal-100">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Our Transparent Pricing Promise — No Surprises, No Gimmicks
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  We built BusinessWebsitePro with one mission: deliver high-quality websites without confusing pricing, hidden fees, or forced subscriptions.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-white p-5 rounded-2xl shadow-sm">
                <i className="ri-check-line text-2xl text-teal-500 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Flat upfront pricing</h3>
                  <p className="text-sm text-gray-600">No hidden fees</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-5 rounded-2xl shadow-sm">
                <i className="ri-check-line text-2xl text-teal-500 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Pay after approval</h3>
                  <p className="text-sm text-gray-600">Only after you approve your homepage preview</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-5 rounded-2xl shadow-sm">
                <i className="ri-check-line text-2xl text-teal-500 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">No forced monthly plans</h3>
                  <p className="text-sm text-gray-600">Choose what works for you</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-5 rounded-2xl shadow-sm">
                <i className="ri-check-line text-2xl text-teal-500 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Optional add-ons</h3>
                  <p className="text-sm text-gray-600">Only if YOU choose them</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-5 rounded-2xl shadow-sm">
                <i className="ri-check-line text-2xl text-teal-500 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Full website ownership</h3>
                  <p className="text-sm text-gray-600">For life</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-5 rounded-2xl shadow-sm">
                <i className="ri-check-line text-2xl text-teal-500 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Clear breakdown</h3>
                  <p className="text-sm text-gray-600">What is included and what is not</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl text-white text-center">
              <p className="text-lg font-semibold">
                This is our commitment to honesty and fairness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What's Included — And What's Not
            </h2>
            <p className="text-lg text-gray-600">
              We want you to know exactly what you're getting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Included */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-green-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Included in Every Package</h3>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">All pages shown in your chosen package</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Professional mobile-friendly design</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">SEO basics for better visibility</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Contact forms</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Social media integration </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Support period included</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Fast and secure setup</span>
                </li>
              </ul>
            </div>

            {/* Not Included */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-orange-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <i className="ri-information-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Not Included (Unless You Add Them)</h3>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-xl text-orange-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Extra products for e-commerce</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-xl text-orange-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Additional blog posts</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-xl text-orange-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Booking system integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-xl text-orange-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Premium hosting or large email setup</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-xl text-orange-500 flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Advanced custom work outside package scope</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xl font-semibold text-gray-900">
              No surprises. No automatic charges. Everything is clearly explained before you commit.
            </p>
          </div>
        </div>
      </section>

      {/* No-Upselling Guarantee */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-purple-50 to-white rounded-3xl p-10 md:p-16 shadow-lg border border-purple-200 text-center">
            <div className="w-20 h-20 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-star-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              No Upselling. No Hidden Fees. Guaranteed.
            </h2>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              We never push you to buy features you don't need.
            </p>
            <div className="bg-white rounded-2xl p-8 shadow-md border-2 border-purple-300">
              <p className="text-2xl font-bold text-purple-600 mb-4">
                "You choose the features that make sense for your business. Nothing more."
              </p>
              <p className="text-gray-700 leading-relaxed">
                Many agencies lure clients with low prices and then add hidden charges later. We don't. Your price stays exactly what you see.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Clear answers to your most important questions
            </p>
          </div>

          <div className="space-y-6">
            {/* Question 1 - Cancellation Policy */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    What is your cancellation or termination policy?
                  </h3>
                  <div className="space-y-3 leading-relaxed">
                    <p className="font-bold text-lg text-gray-900">You can cancel anytime with zero penalties.</p>
                    <p className="font-bold text-gray-900">You only pay for work that has been completed or approved. There are no hidden termination fees, no cancellation charges, and no long-term contracts locking you in.</p>
                    <ul className="space-y-2 mt-4">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-500 mt-1"></i>
                        <span className="font-bold text-gray-900">No termination fees</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-500 mt-1"></i>
                        <span className="font-bold text-gray-900">No cancellation penalties</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Pay only for completed work</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-500 mt-1"></i>
                        <span className="font-bold text-gray-900">No forced commitments</span>
                      </li>
                    </ul>
                    <p className="mt-6 font-bold text-teal-600">
                      Your satisfaction and peace of mind come first.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 2 - Full Website Ownership */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-line text-2xl text-purple-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    What exactly does "Full Website Ownership" mean?
                  </h3>
                  <div className="space-y-3 leading-relaxed">
                    <p className="font-bold text-lg text-gray-900">You own everything — 100%.</p>
                    <p className="font-bold text-gray-900">When your website is complete, you own all the code, all the content, all the design files, and all the rights. You can move your website to any hosting provider at any time, with no restrictions.</p>
                    <ul className="space-y-2 mt-4">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Complete ownership of all code and files</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-500 mt-1"></i>
                        <span className="font-bold text-gray-900">No ongoing usage fees or licensing costs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Freedom to move to any hosting provider</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-500 mt-1"></i>
                        <span className="font-bold text-gray-900">No vendor lock-in whatsoever</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Full access to modify or update anytime</span>
                      </li>
                    </ul>
                    <p className="mt-6 font-bold text-purple-600">
                      Your website is yours forever — no strings attached.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 3 - Price Stability */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-line text-2xl text-orange-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Will my project price ever change after I approve the quote?
                  </h3>
                  <div className="space-y-3 leading-relaxed">
                    <p className="font-bold text-lg text-gray-900">No. Your quote is a guaranteed fixed rate.</p>
                    <p className="font-bold text-gray-900">Once you approve your quote, that price is locked in. We will never increase the price unless you formally request changes to the project scope.</p>
                    <ul className="space-y-2 mt-4">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Fixed pricing — no surprise increases</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Price changes only if YOU request scope changes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-500 mt-1"></i>
                        <span className="font-bold text-gray-900">All changes must be formally approved by you</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-500 mt-1"></i>
                        <span className="font-bold text-gray-900">No hidden costs or last-minute fees</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-500 mt-1"></i>
                        <span className="font-bold text-gray-900">Complete price transparency from start to finish</span>
                      </li>
                    </ul>
                    <p className="mt-6 font-bold text-orange-600">
                      What you see is what you pay — guaranteed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Existing FAQ Section - Are there any hidden fees? */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-10 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-question-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Are there any hidden fees?
                </h3>
                <div className="space-y-3 leading-relaxed">
                  <p className="font-bold text-lg text-gray-900">Absolutely not.</p>
                  <p className="font-bold text-gray-900">Our pricing is fully honest and transparent.</p>
                  <p className="font-bold text-gray-900">What you see is exactly what you pay.</p>
                  <ul className="space-y-2 mt-4">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-1"></i>
                      <span className="font-bold text-gray-900">No surprise invoices.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-1"></i>
                      <span className="font-bold text-gray-900">No subscription tricks.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-1"></i>
                      <span className="font-bold text-gray-900">No forced long-term commitments.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-500 mt-1"></i>
                      <span className="font-bold text-gray-900">You pay only for extras if you choose them.</span>
                    </li>
                  </ul>
                  <p className="mt-6 font-bold text-teal-600">
                    We built our business on trust — and we protect that trust fiercely.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why We're Different — Simple Comparison
            </h2>
            <p className="text-lg text-gray-600">
              This is why businesses prefer us.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
                <tr>
                  <th className="px-6 py-5 text-left text-lg font-bold">Feature</th>
                  <th className="px-6 py-5 text-center text-lg font-bold">BusinessWebsitePro</th>
                  <th className="px-6 py-5 text-center text-lg font-bold">Typical Agencies</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((item, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-5 font-semibold text-gray-900">{item.feature}</td>
                    <td className="px-6 py-5 text-center">
                      {item.us ? (
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-check-line text-3xl text-green-500"></i>
                          <span className="font-semibold text-green-600">Yes</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-close-line text-3xl text-red-400"></i>
                          <span className="font-semibold text-red-400">No</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-5 text-center">
                      {item.others ? (
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-check-line text-3xl text-green-500"></i>
                          <span className="font-semibold text-green-600">Yes</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-close-line text-3xl text-red-400"></i>
                          <span className="font-semibold text-red-400">No</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xl font-semibold text-gray-900">
              We offer the most honest, predictable, and customer-friendly pricing in the industry.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Real Customer Experiences
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-lg text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.business}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-500 via-teal-600 to-teal-500">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to see your website before paying?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Request your Free Homepage Preview — no risk, no payment required.
          </p>
          <button
            onClick={() => setShowPreviewForm(true)}
            className="cursor-pointer inline-flex items-center gap-3 bg-white text-teal-600 px-10 py-5 rounded-full font-bold text-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
          >
            <i className="ri-gift-line text-2xl"></i>
            Get My Free Homepage Preview
          </button>
          <p className="text-white/80 mt-6">
            <i className="ri-shield-check-line mr-2"></i>
            No credit card required • No obligations • 100% free preview
          </p>
        </div>
      </section>

      {/* Preview Form Modal */}
      {showPreviewForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <button
                onClick={() => setShowPreviewForm(false)}
                className="cursor-pointer float-right w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>

              <div className="mb-8">
                <div className="inline-flex items-center gap-2 bg-[#14B8A6]/10 text-[#14B8A6] px-4 py-2 rounded-full font-semibold text-sm mb-4">
                  <i className="ri-gift-line"></i>
                  FREE PREVIEW REQUEST
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Get Your Free Homepage Preview</h3>
                <p className="text-gray-600">Fill out the form below and we'll create a custom homepage preview for your business — completely free!</p>
              </div>

              {formSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-green-500"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Request Submitted!</h4>
                  <p className="text-gray-600">We'll start working on your free homepage preview and contact you within 24 hours.</p>
                </div>
              ) : (
                <form id="transparency-preview-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="Your Business Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type of Service/Business <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#14B8A6] focus:outline-none transition-colors text-base"
                    >
                      <option value="">Select your business type</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="Retail Store">Retail Store</option>
                      <option value="Professional Services">Professional Services</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Construction">Construction</option>
                      <option value="Beauty & Wellness">Beauty &amp; Wellness</option>
                      <option value="Education">Education</option>
                      <option value="E-commerce">E-commerce</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="cursor-pointer w-full bg-gradient-to-r from-[#14B8A6] to-[#0d9488] text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap"
                  >
                    <i className="ri-send-plane-fill mr-2"></i>
                    Submit Request
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to be contacted about your free homepage preview.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TransparencyPage;
